import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#currentMediaId == null) this.#currentMediaId = this.dataset.currentMediaId || null;

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /** @type {string | null} The media id of the variant currently reflected in the gallery. */
  #currentMediaId = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const newMediaId = event.detail.resource?.featured_media?.id;

    // Only react when the variant's image actually changes (i.e. a colour change).
    // Length/weight variants share the same featured image, so leave the gallery
    // exactly where the shopper left it — no reset to the first slide.
    if (newMediaId == null) return;
    if (this.#currentMediaId != null && String(newMediaId) === String(this.#currentMediaId)) return;
    this.#currentMediaId = String(newMediaId);

    const source = event.detail.data.html;
    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    // Swap in the re-rendered gallery (brings the new colour's image into the DOM
    // when unselected variant media are hidden), then jump to that colour's slide —
    // media renders in natural order now, so it isn't necessarily the first slide.
    this.replaceWith(newMediaGallery);
    requestAnimationFrame(() => {
      const slides = Array.from(newMediaGallery.querySelectorAll('slideshow-slide'));
      const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${newMediaId}"]`));
      if (index > -1) newMediaGallery.slideshow?.select(index, undefined, { animate: false });
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
